import { motion, useAnimation, useInView } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as FiverrIcon } from '../../assets/icons/brand-fiverr.svg';
import { ReactComponent as EmailIcon } from '../../assets/icons/brand-gmail.svg';
import { ReactComponent as LinkedInIcon } from '../../assets/icons/brand-linkedin.svg';
import { ReactComponent as LocationIcon } from '../../assets/icons/location.svg';
import './contact.css';
import { contactInfo, fetchStatus } from './Data'; // Mengimpor data dan fungsi fetchStatus

const Contact = () => {
  const [status, setStatus] = useState('Available');  // Status default
  const [loading, setLoading] = useState(true);  // Menandakan sedang memuat status
  const ref = useRef(null);
  const isInView = useInView(ref, { 
    once: false,
    threshold: 0.2,
    margin: "-100px"
  });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    } else {
      controls.start('hidden');
    }
  }, [isInView, controls]);

  const containerVariants = {
    hidden: { 
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0,
      y: 50,
      scale: 0.9
    },
    visible: i => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 100,
        delay: i * 0.2,
        duration: 0.8
      }
    })
  };

  // Mengambil data status dari Supabase
  useEffect(() => {
    const getStatusFromDB = async () => {
      const statusFromDB = await fetchStatus(); // Mendapatkan status dari Supabase
      if (statusFromDB) {
        setStatus(statusFromDB);  // Mengupdate status
      }
      setLoading(false);  // Mengatur loading menjadi false setelah data didapat
    };

    getStatusFromDB();
  }, []);

  const googleMapsUrl = `https://www.google.com/maps?q=${encodeURIComponent(contactInfo.address)}`;

  // Fungsi untuk menentukan kelas status berdasarkan status
  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'open to work':
        return 'status-badge status-open';
      case 'occupied':
        return 'status-badge status-occupied';
      case 'offline':
        return 'status-badge status-out-of-service';
      default:
        return 'status-badge status-unknown';
    }
  };

  return (
    <motion.section 
      className="contact section" 
      id="contact"
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={containerVariants}
    >
      <motion.h2 
        className="section__title"
        variants={cardVariants}
        custom={0}
      >
        Contact
      </motion.h2>
      <motion.span 
        className="section__subtitle"
        variants={cardVariants}
        custom={1}
      >
        Get in touch
      </motion.span>

      <motion.div 
        className="contact__container container grid"
        variants={containerVariants}
      >
        <motion.div 
          className="contact__content"
          variants={cardVariants}
          custom={2}
        >
          <motion.h3 
            className="contact__title"
            variants={cardVariants}
          >
            Talk to me
          </motion.h3>

          <motion.div 
            className="contact__info"
            variants={containerVariants}
          >
            {/* Card untuk Email */}
            <motion.div 
              className="contact__card"
              variants={cardVariants}
              custom={3}
              whileHover={{ 
                scale: 1.05,
                transition: { type: "spring", stiffness: 300 }
              }}
            >
              <EmailIcon className="contact__card-icon" />
              <h3 className="contact__card-title">Email</h3>
              <span className="contact__card-detail">{contactInfo.email}</span>
              <br />
              <a href={`mailto:${contactInfo.email}`} className="contact__button">
                Contact Me <i className="bx bx-right-arrow-alt contact__button-icon"></i>
              </a>
            </motion.div>

            {/* Card untuk LinkedIn */}
            <motion.div 
              className="contact__card"
              variants={cardVariants}
              custom={4}
              whileHover={{ 
                scale: 1.05,
                transition: { type: "spring", stiffness: 300 }
              }}
            >
              <LinkedInIcon className="contact__card-icon" />
              <h3 className="contact__card-title">LinkedIn</h3>
              <span className="contact__card-detail">Alvin Ahmad</span>
              <br />
              <a href={contactInfo.linkedin} className="contact__button" target="_blank" rel="noopener noreferrer">
                Visit Profile <i className="bx bx-right-arrow-alt contact__button-icon"></i>
              </a>
            </motion.div>

            {/* Card untuk Fiverr */}
            <motion.div 
              className="contact__card"
              variants={cardVariants}
              custom={5}
              whileHover={{ 
                scale: 1.05,
                transition: { type: "spring", stiffness: 300 }
              }}
            >
              <FiverrIcon className="contact__card-icon" />
              <h3 className="contact__card-title">Fiverr</h3>
              <span className="contact__card-detail">{contactInfo.fiver}</span>
              <br />
              <a href={`https://www.fiverr.com/${contactInfo.fiver}`} className="contact__button">
                Hire me <i className="bx bx-right-arrow-alt contact__button-icon"></i>
              </a>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Bagian Status dan Alamat dalam 1 Card */}
        <motion.div 
          className="contact__content"
          variants={cardVariants}
          custom={6}
        >
          <motion.h3 
            className="contact__title"
            variants={cardVariants}
          >
            Status
          </motion.h3>

          <motion.div 
            className="contact__info"
            variants={containerVariants}
          >
            {/* Card untuk Status */}
            <motion.div 
              className="contact__card contact__card--status"
              variants={cardVariants}
              custom={4}
              whileHover={{ scale: 1.05 }}
            >
              <i className="bx bx-briefcase contact__card-icon"></i>
              <h3 className="contact__card-title">Work Status</h3>
              <br />
              <br />
              {/* Tampilkan status, jika masih loading tampilkan "Loading..." */}
              <span className="contact__card-data">
                {loading ? 'Loading...' : (
                  <span className={getStatusBadgeClass(status)}>
                    {status}
                  </span>
                )}
              </span>
              <br />
            </motion.div>

            {/* Card untuk Alamat */}
            <motion.div 
              className="contact__card contact__card--address"
              variants={cardVariants}
              custom={5}
              whileHover={{ scale: 1.05 }}
            >
              <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
                <LocationIcon className="contact__card-icon" />
                <h3 className="contact__card-title">Address</h3>
                <span className="contact__card-data">{contactInfo.address}</span>
              </a>
            </motion.div>
          </motion.div>
        </motion.div>

      </motion.div>
    </motion.section>
  );
};

export default Contact;
